import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Plus, Trash2, Languages } from "lucide-react";
import { PageHead, Panel } from "./AdminLayout";
import ImageUpload from "./ImageUpload";
import TranslateDialog from "./TranslateDialog";
import { useSite } from "@/context/SiteContext";

function RowList({ items, cols, onChange, testid }) {
  const upd = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          {cols.map(([k, ph, w]) => <input key={k} value={it[k] || ""} placeholder={ph} onChange={(e) => upd(i, k, e.target.value)} className={`border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#D97706] ${w || "flex-1"}`} data-testid={`${testid}-${k}-${i}`} />)}
          <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-2 text-rose-600" data-testid={`${testid}-delete-${i}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, Object.fromEntries(cols.map(([k]) => [k, ""]))])} className="inline-flex items-center gap-1 text-xs font-semibold text-[#D97706]" data-testid={`${testid}-add`}><Plus size={12} /> Tambah</button>
    </div>
  );
}

const FIELDS = [["company_name", "Nama Perusahaan"], ["tagline", "Tagline"], ["hero_title", "Judul Hero (Landing Page)", "area"], ["hero_subtitle", "Subjudul Hero", "area"], ["about", "Tentang Perusahaan", "area"], ["address", "Alamat"], ["email", "Email"], ["phone", "Telepon"], ["hours", "Jam Operasional"]];
const cls = "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#D97706]";

export default function CompanySettings() {
  const [p, setP] = useState(null);
  const [notif, setNotif] = useState(null);
  const [tr, setTr] = useState(false);
  const [trFields, setTrFields] = useState([]);
  const { refresh } = useSite();
  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setP(r.data.profile);
      setNotif(r.data.notifications || { enabled: true, emails: [] });
    });
    api.get("/admin/translatable").then((r) => setTrFields(r.data.fields.profile));
  }, []);
  const save = async (profile = p) => {
    try {
      await api.put("/admin/settings", { profile, notifications: notif });
      toast.success("Disimpan sebagai draft — klik Publish ke Website untuk menayangkan");
      refresh();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!p || !notif) return <p className="font-mono text-sm text-slate-500">Memuat...</p>;
  const setStat = (i, k, v) => setP({ ...p, stats: p.stats.map((s, j) => (j === i ? { ...s, [k]: v } : s)) });
  return (
    <div data-testid="admin-settings">
      <PageHead eyebrow="CMS" title="Profil Perusahaan & Landing Page" desc="Konten hero, profil, kontak, dan statistik di halaman utama.">
        <button onClick={() => setTr(true)} className="inline-flex items-center gap-2 border border-[#0284C7] bg-white px-4 py-2.5 text-sm font-bold text-[#0284C7] hover:bg-sky-50" data-testid="settings-translate-button"><Languages size={16} /> Terjemahan (EN / 中文)</button>
        <button onClick={() => save()} className="bg-[#D97706] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#B45309]" data-testid="settings-save-button">Simpan Perubahan</button>
      </PageHead>
      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Informasi" className="lg:col-span-2">
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map(([k, l, t]) => (
              <label key={k} className={`text-xs font-semibold text-slate-600 ${t ? "sm:col-span-2" : ""}`}>{l}
                {t ? <textarea rows={3} value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className={cls} data-testid={`settings-${k}`} />
                  : <input value={p[k] || ""} onChange={(e) => setP({ ...p, [k]: e.target.value })} className={cls} data-testid={`settings-${k}`} />}
              </label>
            ))}
          </div>
        </Panel>
        <Panel title="Logo & Foto">
          <div className="space-y-4">
            <div className="text-xs font-semibold text-slate-600">Logo Perusahaan (PNG/SVG transparan disarankan)<ImageUpload value={p.logo_url} onChange={(v) => setP({ ...p, logo_url: v })} testid="settings-logo-upload" label="Unggah Logo" /></div>
            <div className="text-xs font-semibold text-slate-600">Foto Tentang Kami<ImageUpload value={p.about_image} onChange={(v) => setP({ ...p, about_image: v })} testid="settings-about-image-upload" /></div>
          </div>
        </Panel>
        <Panel title="Visi & Misi" className="lg:col-span-2">
          <label className="block text-xs font-semibold text-slate-600">Visi<textarea rows={2} value={p.vision || ""} onChange={(e) => setP({ ...p, vision: e.target.value })} className={cls} data-testid="settings-vision" /></label>
          <label className="mt-3 block text-xs font-semibold text-slate-600">Misi (1 per baris)<textarea rows={4} value={(p.mission || []).join("\n")} onChange={(e) => setP({ ...p, mission: e.target.value.split("\n") })} className={cls} data-testid="settings-mission" /></label>
        </Panel>
        <Panel title="Nilai / Keunggulan">
          <RowList items={p.values || []} cols={[["title", "Judul", "w-1/3"], ["desc", "Deskripsi"]]} onChange={(v) => setP({ ...p, values: v })} testid="settings-values" />
        </Panel>
        <Panel title="Legalitas & Sertifikasi Perusahaan" className="lg:col-span-3">
          <RowList items={p.legal || []} cols={[["name", "Nama dokumen (mis. NIB, ISO 9001)"], ["number", "Nomor", "w-48"], ["issuer", "Penerbit", "w-48"]]} onChange={(v) => setP({ ...p, legal: v })} testid="settings-legal" />
        </Panel>
        <Panel title="Statistik Hero">
          <div className="space-y-3">
            {p.stats.map((s, i) => (
              <div key={i} className="grid grid-cols-3 gap-2">
                <input value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} className={cls} data-testid={`settings-stat-value-${i}`} />
                <input value={s.label} onChange={(e) => setStat(i, "label", e.target.value)} className={`${cls} col-span-2`} data-testid={`settings-stat-label-${i}`} />
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <TranslateDialog open={tr} onClose={() => setTr(false)} kind="profile" item={p} fields={trFields}
        onSaved={(i18n, manual) => { const np = { ...p, i18n }; setP(np); if (manual) save(np); else refresh(); }} />
      <Panel title="Notifikasi Email Lead" className="mt-6">
        <div className="max-w-2xl space-y-3">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!notif.enabled} onChange={(e) => setNotif({ ...notif, enabled: e.target.checked })} data-testid="notif-enabled-toggle" /> Aktifkan notifikasi email untuk lead baru (formulir website + pengunjung chatbot yang mengisi nama & WhatsApp)</label>
          <label className="block text-xs font-semibold text-slate-600">Email penerima (1 per baris)
            <textarea rows={3} value={(notif.emails || []).join("\n")} onChange={(e) => setNotif({ ...notif, emails: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} className={cls} data-testid="notif-emails-input" />
          </label>
          <p className="text-[11px] text-slate-500">Email berisi nama, perusahaan, nomor WhatsApp, layanan, dan tombol menuju Lead Tracker.</p>
        </div>
      </Panel>
    </div>
  );
}
